import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Paper,
  CircularProgress,
  image,
  Divider,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { getAgentByIdService } from "../../services/agentService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const getContactLabel = (contactType) => {
  switch (contactType) {
    case "phone":
      return "Phone Number";
    case "atl1":
      return "Alternate Phone Number 1";
    case "atl2":
      return "Alternate Phone Number 2";
    case "email":
      return "Email";
    default:
      return "Unknown Contact Type";
  }
};

const AgentDetails = () => {
  const { id } = useParams(); // Get the agent ID from the URL
  const [agent, setAgent] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const handleBackClick = () => {
    navigate(-1); // Navigate to the previous page
  };
  const fetchAgentDetails = async () => {
    try {
      const response = await getAgentByIdService(id);
      setAgent(response);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  const handleImageClick = (imageSrc) => {
    setPreviewImage(imageSrc);
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  useEffect(() => {
    fetchAgentDetails();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ padding: 4 }}>
      {agent && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex" }}>
              <ArrowBackIcon
                onClick={handleBackClick}
                style={{ cursor: "pointer" }}
              />
              <Typography variant="h6" sx={{ color: "#5252FF", ml: 1 }}>
                Agent Information
              </Typography>
            </Box>
          </Grid>
          {/* User Information */}
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Name:</strong> {agent.user.userName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Agent Photo:</strong>{" "}
            </Typography>
            <img
              alt={agent.user.userName}
              src={agent.user.userPhoto || "/default-image.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(agent.user.userPhoto || "/default-image.png")
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Divider
              sx={{
                my: 1,
              }}
            />
          </Grid>
          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Contact Information
            </Typography>
          </Grid>
          {agent.contacts.map((contact) => (
            <Grid item xs={12} md={6} key={contact._id}>
              <Typography>
                <strong>{getContactLabel(contact.contactType)}:</strong>{" "}
                {contact.contactValue}
              </Typography>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Divider
              sx={{
                my: 1,
              }}
            />
          </Grid>
          {/* Address Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Address Information
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Address:</strong> {agent.user?.address || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider
              sx={{
                my: 1,
              }}
            />
          </Grid>
          {/* Document Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Documents
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Document:</strong>{" "}
            </Typography>
            <img
              alt={agent.user.userName}
              src={agent.agentDocuments?.document || "/default-image.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  agent.agentDocuments?.document || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Aadhaar Number:</strong>{" "}
              {agent.agentDocuments?.aadhaarNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Aadhaar Image:</strong>{" "}
            </Typography>
            <img
              alt="Aadhaar Photo"
              src={
                agent.agentDocuments?.aadhaarPhoto || "/default-document.png"
              }
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  agent.agentDocuments?.aadhaarPhoto || "/default-document.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>PAN Number:</strong>{" "}
              {agent.agentDocuments?.panNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>PAN Image:</strong>{" "}
            </Typography>
            <img
              alt="Aadhaar Photo"
              src={agent.agentDocuments?.panPhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  agent.agentDocuments?.panPhoto || "/default-document.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Divider
              sx={{
                my: 1,
              }}
            />
          </Grid>
          {/* Document Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Bank Details
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Account Number:</strong>{" "}
              {agent.agentBankDetails?.accountNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Account Name:</strong>{" "}
              {agent.agentBankDetails?.accountName || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Branch:</strong> {agent.agentBankDetails?.branch || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>IFSC:</strong> {agent.agentBankDetails?.IFSC || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider
              sx={{
                my: 1,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Family Details
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Family Person Name:</strong>{" "}
              {agent.agentFamilyDetails?.familyPersonName || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Relation:</strong>{" "}
              {agent.agentFamilyDetails?.relation || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Family Person Address:</strong>{" "}
              {agent.agentFamilyDetails?.familyPersonAddress || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Family Person Phone Number:</strong>{" "}
              {agent.agentFamilyDetails?.familyPersonPhoneNumber || "N/A"}
            </Typography>
          </Grid>
        </Grid>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!previewImage} onClose={handleClosePreview} maxWidth="md">
        <DialogContent>
          <img
            src={previewImage}
            alt="Preview"
            style={{ maxWidth: "6u00px", maxHeight: "700px" }}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default AgentDetails;
